import * as React from "react";
import { useState, useEffect } from "react";
import { Loader2, User } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAddEnrollment } from "@/hooks/use-class-management";
import { useAuth } from "@/hooks/use-auth-compat";
import { apiFetch } from "@/lib/apiFetch";
import { toast } from "sonner";

interface AddToOfferingDialogProps {
  threadId: string;
  open: boolean;
  onClose: () => void;
  preselectedOfferingId?: string;
  /** Student's display name from the inquiry thread */
  studentName?: string | null;
  /** Student's shared email from the inquiry thread (may be null if not shared) */
  studentEmail?: string | null;
}

interface OfferingOption {
  id: string;
  title: string;
}

export default function AddToOfferingDialog({
  threadId,
  open,
  onClose,
  preselectedOfferingId,
  studentName,
  studentEmail,
}: AddToOfferingDialogProps) {
  const auth = useAuth();
  const token = auth.user?.access_token ?? "";

  const [offerings, setOfferings] = useState<OfferingOption[]>([]);
  const [offeringId, setOfferingId] = useState<string>(preselectedOfferingId ?? "");
  const [inlineError, setInlineError] = useState<string>("");

  const addEnrollment = useAddEnrollment();

  // Fetch published offerings when dialog opens
  useEffect(() => {
    if (!open) return;
    setOfferingId(preselectedOfferingId ?? "");
    setInlineError("");

    void (async () => {
      try {
        const res = await apiFetch("/api/v1/offerings?status=PUBLISHED&size=50", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = (await res.json()) as
          | OfferingOption[]
          | { offerings?: OfferingOption[]; content?: OfferingOption[]; items?: OfferingOption[] };
        if (Array.isArray(data)) {
          setOfferings(data);
        } else if (Array.isArray((data as any).offerings)) {
          setOfferings((data as any).offerings as OfferingOption[]);
        } else if (Array.isArray((data as any).content)) {
          setOfferings((data as any).content as OfferingOption[]);
        } else if (Array.isArray((data as any).items)) {
          setOfferings((data as any).items as OfferingOption[]);
        }
      } catch {
        // silently fail
      }
    })();
  }, [open, token]);

  function handleClose() {
    addEnrollment.reset();
    setInlineError("");
    onClose();
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setInlineError("");

    if (!offeringId) {
      setInlineError("Please select an offering.");
      return;
    }

    try {
      // The backend resolves the student from threadId — email is display-only
      await addEnrollment.mutateAsync({ threadId, offeringId });
      toast.success(`${studentName || "Student"} added to offering.`);
      handleClose();
    } catch (err: unknown) {
      const status =
        (err as { status?: number })?.status ??
        (err instanceof Error && err.message.includes("409") ? 409 : 0);
      if (status === 409) {
        setInlineError("Student is already enrolled in this offering.");
      } else {
        setInlineError("Something went wrong. Please try again.");
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add to Offering</DialogTitle>
        </DialogHeader>

        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-4 pt-1">

          {/* Student identity — read-only, derived from the thread */}
          {(studentName || studentEmail) && (
            <div className="flex items-center gap-3 rounded-xl bg-slate-50 border border-slate-200 px-4 py-3">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                {studentName ? (
                  <span className="text-xs font-bold text-indigo-700">
                    {studentName.split(" ").map((w) => w[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
                  </span>
                ) : (
                  <User className="w-4 h-4 text-indigo-400" aria-hidden />
                )}
              </div>
              <div className="min-w-0">
                {studentName && (
                  <p className="text-sm font-semibold text-slate-800 truncate">{studentName}</p>
                )}
                {studentEmail ? (
                  <p className="text-xs text-slate-500 truncate">{studentEmail}</p>
                ) : (
                  <p className="text-xs text-slate-400 italic">Email not shared</p>
                )}
              </div>
            </div>
          )}

          {/* Offering select */}
          <div className="space-y-1.5">
            <Label htmlFor="offering-select">Select offering</Label>
            <Select value={offeringId} onValueChange={(val) => setOfferingId(val)}>
              <SelectTrigger id="offering-select" className="cursor-pointer w-full">
                <SelectValue placeholder="Choose an offering…" />
              </SelectTrigger>
              <SelectContent>
                {offerings.length === 0 ? (
                  <SelectItem value="__none__" disabled>
                    No published offerings
                  </SelectItem>
                ) : (
                  offerings.map((o) => (
                    <SelectItem key={o.id} value={o.id} className="cursor-pointer">
                      {o.title}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Inline error */}
          {inlineError && (
            <p role="alert" className="text-sm font-medium text-destructive">
              {inlineError}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} className="cursor-pointer">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={addEnrollment.isPending}
              className="cursor-pointer bg-indigo-600 hover:bg-indigo-700 text-white"
            >
              {addEnrollment.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Add as pending
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
